/**
 * Single source of truth for rendering an AEP section by key.
 *
 * Used by:
 *  - `CnpjAEPReport.jsx` (final report / PDF view)
 *  - `CnpjAEPEditor.jsx` (interactive editor with reorder + break controls)
 *
 * Keeping this in one place avoids drift between the report and the editor's
 * preview — every change to a section's props automatically reflects in both.
 */
import {
  AEPCover, AEPSummary, AEPDashboard, AEPControleDoc, AEPResumoExec,
  AEPPrioridades, AEPRastreabilidade, AEPHeatmap, AEPTimeline,
  AEPChecklist, AEPLgpd, AEPIntro, AEPBase, AEPConsolidacao,
  AEPMatriz, AEPMatrizConsolidada, AEPPlano, AEPPlanoAnual,
  AEPAbsenteismo, AEPRecomendacoesIA,
  AEPConclusao, AEPAssinaturas,
} from "@/pages/aep/AEPSections";

export function renderAEPSection(key, ctx) {
  const {
    a, company, date, hash, included, data, score100, criticalCount, focoDim,
    ranked, critByDim, maxResp, timelineBuckets, actions, responses, cnpj,
    overrides, onOverrideChange, onActionsChanged, attachments, onAttachmentsChange,
    raw, matrizViews,
  } = ctx;
  switch (key) {
    case "capa":            return <AEPCover key={key}            a={a} company={company} date={date} hash={hash} />;
    case "sumario":         return <AEPSummary key={key}          included={included} />;
    case "dashboard":       return <AEPDashboard key={key}        data={data} score100={score100} criticalCount={criticalCount} />;
    case "controle_doc":    return <AEPControleDoc key={key}      a={a} company={company} date={date} hash={hash} />;
    case "resumo":          return <AEPResumoExec key={key}       data={data} score100={score100} criticalCount={criticalCount} focoDim={focoDim} />;
    case "prioridades":     return <AEPPrioridades key={key}      ranked={ranked} />;
    case "rastreabilidade": return <AEPRastreabilidade key={key}  ranked={ranked} cnpj={company?.cnpj || cnpj} />;
    case "heatmap":         return <AEPHeatmap key={key}          ranked={ranked} critByDim={critByDim} maxResp={maxResp} />;
    case "timeline":        return <AEPTimeline key={key}         timelineBuckets={timelineBuckets} />;
    case "checklist":       return <AEPChecklist key={key}        hasActions={actions.length > 0} />;
    case "lgpd":            return <AEPLgpd key={key} />;
    case "intro":           return <AEPIntro key={key} />;
    case "base":            return <AEPBase key={key}             responses={responses} totalQuestions={data?.questions?.length} />;
    case "consolidacao":    return <AEPConsolidacao key={key}     data={data} />;
    case "matriz":          return <AEPMatriz key={key}           data={data} criticalCount={criticalCount} matrixType={a.matrix_type || "pxs"} expectedWorkers={a.n_trabalhadores || 0} />;
    case "matriz_consolidada":
      return (
        <AEPMatrizConsolidada key={key}
          geral={{
            dimensions: data.dimensions,
            overall_score: data.overall_score,
            overall: data.overall,
            response_count: data.response_count,
            critical_count: criticalCount,
          }}
          byGhe={raw?.by_ghe || []}
          perAssessment={raw?.per_assessment || []}
          views={matrizViews || ["geral", "ghe", "individual"]}
          matrixType={a.matrix_type || "pxs"}
          expectedWorkers={a.n_trabalhadores || 0}
        />
      );
    case "plano":            return <AEPPlano key={key}            actions={actions} cnpj={cnpj} overrides={overrides} onOverrideChange={onOverrideChange} onActionsChanged={onActionsChanged} />;
    case "recomendacoes_ia": return <AEPRecomendacoesIA key={key}  assessmentId={a?.id} assessmentIds={(raw?.assessments || []).map((x) => x.id).filter(Boolean)} cnpj={cnpj} overrides={overrides} onOverrideChange={onOverrideChange} />;
    case "plano_anual":      return <AEPPlanoAnual key={key}       companyId={company?.id || a?.company_id} cnpj={cnpj} overrides={overrides} onOverrideChange={onOverrideChange} attachments={attachments} onAttachmentsChange={onAttachmentsChange} />;
    case "absenteismo":      return <AEPAbsenteismo key={key}      companyId={company?.id || a?.company_id} cnpj={cnpj} overrides={overrides} onOverrideChange={onOverrideChange} attachments={attachments} onAttachmentsChange={onAttachmentsChange} />;
    case "conclusao":        return <AEPConclusao key={key}        data={data} score100={score100} criticalCount={criticalCount} cnpj={cnpj} overrides={overrides} onOverrideChange={onOverrideChange} />;
    case "assinaturas":      return <AEPAssinaturas key={key}      a={a} company={company} date={date} hash={hash} />;
    default:                 return null;
  }
}
